import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Download, Loader2, ImageOff, X } from 'lucide-react';
import { fetchStorageDataUrl, type StorageSize } from '../../api/dataApi';
import { queryClient } from '../../lib/queryClient';
import type { MessageAttachment } from '../../types';

// Fetches a stored blob rendition as a data: URL, cached forever - a
// /storage key is content-addressed and immutable (see uploadAttachment), so
// unlike every other query in this app a cached result here never needs a
// refetch. Mirrors AvatarView.tsx's useAvatarImageUrl.
//
// `size` is which rendition (see StorageSize): the chat bubble shows the
// 512 px `medium`, the lightbox the 1280 px `large`, and only an explicit
// Download fetches the `original`. Each is its own cache entry, and the
// server falls back to the original itself when a rendition doesn't exist.
function useStorageDataUrl(key: string | undefined, size: StorageSize, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(
    key ? (queryClient.getQueryData<string | null>(['storageBlob', key, size]) ?? null) : null
  );
  useEffect(() => {
    if (!key || !enabled) return;
    const cached = queryClient.getQueryData<string | null>(['storageBlob', key, size]);
    if (cached !== undefined) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    queryClient
      .query({
        queryKey: ['storageBlob', key, size],
        queryFn: () => fetchStorageDataUrl(key, size),
        staleTime: Infinity,
        gcTime: Infinity,
        // The fetch already polls a file that is still being scanned; if it
        // gives up, leave the query un-cached so the next mount retries.
        retry: false,
      })
      .then((dataUrl) => {
        if (!cancelled) setUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [key, size, enabled]);
  return url;
}

function humanSize(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))} KB`;
  return `${bytes} B`;
}

interface AttachmentViewProps {
  attachment: MessageAttachment;
  // Compact = a small pill (used inside a signal medallion row); default
  // is the full inline image / file card for a plain message bubble.
  compact?: boolean;
}

export function AttachmentView({ attachment, compact }: AttachmentViewProps) {
  const isImage = attachment.type.startsWith('image/');
  const [lightbox, setLightbox] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // The bubble shows the medium rendition; the lightbox upgrades to large
  // (falling back to whatever is already loaded while it arrives). Files
  // and Download always fetch the untouched original, on click.
  const inlineUrl = useStorageDataUrl(attachment.key, 'medium', isImage);
  const largeUrl = useStorageDataUrl(attachment.key, 'large', lightbox);
  const dataUrl = inlineUrl;

  const triggerDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const original = await queryClient.fetchQuery({
        queryKey: ['storageBlob', attachment.key, 'original'],
        queryFn: () => fetchStorageDataUrl(attachment.key, 'original'),
        staleTime: Infinity,
        gcTime: 60_000,
        retry: false,
      });
      if (original) {
        const a = document.createElement('a');
        a.href = original;
        a.download = attachment.name || 'download';
        a.click();
      }
    } catch {
      // still being scanned / unreachable - the button just re-enables
    } finally {
      setDownloading(false);
    }
  };

  if (isImage) {
    return (
      <>
        <div
          className={`group relative inline-block overflow-hidden rounded-xl border border-black/10 dark:border-white/15 ${compact ? 'max-w-[160px]' : 'max-w-full'}`}
        >
          {dataUrl ? (
            <img
              src={dataUrl}
              alt={attachment.name}
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(true);
              }}
              className={`block w-auto cursor-zoom-in ${compact ? 'max-h-32' : 'max-h-60'} object-cover`}
            />
          ) : (
            <span className="flex items-center justify-center gap-1.5 px-3 py-6 text-xs text-black/40 dark:text-white/40">
              <Loader2 size={14} className="animate-spin" /> {attachment.name}
            </span>
          )}
          {/* Always-visible download affordance on the thumbnail itself,
              so the file can be saved without opening the lightbox first.
              opacity-100 on touch / focus / hover so it's discoverable
              everywhere, not only on a mouse hover. */}
          <button
            type="button"
            aria-label={`Download ${attachment.name}`}
            onClick={(e) => {
              e.stopPropagation();
              triggerDownload();
            }}
            className="absolute top-1.5 right-1.5 rounded-lg bg-black/55 p-1.5 text-white opacity-80 backdrop-blur-sm transition-opacity hover:bg-black/70 hover:opacity-100 focus-visible:opacity-100"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          </button>
        </div>
        <Lightbox
          open={lightbox}
          onClose={() => setLightbox(false)}
          src={largeUrl ?? dataUrl}
          name={attachment.name}
          onDownload={triggerDownload}
          sizeLabel={humanSize(attachment.size)}
        />
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        triggerDownload();
      }}
      className="flex items-center gap-2.5 rounded-xl border border-black/10 dark:border-white/15 bg-black/[0.03] dark:bg-white/5 px-3 py-2 text-left max-w-full"
    >
      <FileText size={20} className="shrink-0 text-black/40 dark:text-white/40" />
      <span className="flex flex-col min-w-0">
        <span className="text-[13px] font-medium truncate">{attachment.name}</span>
        <span className="text-[11px] text-black/40 dark:text-white/40">{humanSize(attachment.size)}</span>
      </span>
      {downloading ? (
        <Loader2 size={14} className="animate-spin shrink-0" />
      ) : (
        <Download size={14} className="shrink-0 text-black/30 dark:text-white/30" />
      )}
    </button>
  );
}

// A full-viewport image viewer, portalled onto document.body so no
// transformed/overflow-clipped ancestor (a message bubble's
// active:scale, the chat pane's overflow-y-auto) can scope its
// position:fixed - which is what made it render cramped inside the
// narrow Messages column. Click the backdrop or press Escape to close;
// click the image to toggle a 2x zoom, drag to pan while zoomed.
function Lightbox({
  open,
  onClose,
  src,
  name,
  onDownload,
  sizeLabel,
}: {
  open: boolean;
  onClose: () => void;
  src: string | null;
  name: string;
  onDownload: () => void;
  sizeLabel: string;
}) {
  const [zoomed, setZoomed] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!open) return;
    setZoomed(false);
    setPan({ x: 0, y: 0 });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/90" onClick={onClose}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white/90" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm truncate">{name}</span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onDownload}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/25 px-3 py-1.5 text-xs hover:bg-white/10"
          >
            <Download size={14} /> {sizeLabel}
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 hover:bg-white/10">
            <X size={20} />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden p-2">
        {src ? (
          <img
            src={src}
            alt={name}
            draggable={false}
            onClick={(e) => {
              e.stopPropagation();
              setZoomed((z) => !z);
              setPan({ x: 0, y: 0 });
            }}
            onMouseMove={(e) => {
              if (!zoomed || e.buttons !== 1) return;
              setPan((p) => ({ x: p.x + e.movementX, y: p.y + e.movementY }));
            }}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomed ? 2 : 1})`,
              cursor: zoomed ? 'grab' : 'zoom-in',
              transition: 'transform 120ms ease-out',
            }}
            className="max-h-full max-w-full object-contain select-none"
          />
        ) : (
          <ImageOff size={40} className="text-white/40" />
        )}
      </div>
    </div>,
    document.body
  );
}
